import {
  createTarget,
  uploadTargetFile,
  uploadFile
} from '@/api'
import Toast from '@/components/common/toasts/'
import Promise from 'bluebird'
import {
  ATT_STATUS,
  VIEWING_STREAMS,
  ATT_SELECT_TYPE_OPTIONS,
  ACTIVATION_STATUS,
  TARGET_TYPE
} from '@/appLogic/targets/Constants'
import AttAPI from '@/api/AttAPI'
import {cloneDeep} from 'lodash'
import RecurringToast from '@/components/common/toasts/RecurringToast'

export const createATTCreation = function (
  {target, isNew, activate, submitted, submittingOrHasSubmittedFile},
  {afterSave, afterCreate, afterUpload, afterSubmit}
  ) {
  const df = () => {}
  const ATTCreation = {
    target,
    isNew,
    activate,
    submitted,
    submittingFile: submittingOrHasSubmittedFile,
    afterSave: afterSave || df,
    afterCreate: afterCreate || df,
    afterUpload: afterUpload || df,
    afterSubmit: afterSubmit || df,
    run () {
      if (this.activate) {
        return this.activateTarget()
      } else {
        return this.submitTarget()
      }
    },
    activateTarget () {
      return this.createUpdateTarget().then(createdTarget => {
        const ppp = this.uploadTargetFile(createdTarget)
        return ppp.then(() => {
          return createdTarget
        })
      })
    },
    submitTarget () {
      return this.activateTarget().then(createdTarget => {
        let submitTarget = cloneDeep(createdTarget)
        return this.submitTargetToATT(submitTarget).then()
      })
    },
    createUpdateTarget () {
      console.log({targetToCreate: this.target})
      return createTarget(this.target).then((response) => {
        const createdTarget = response.data
        if (this.isNew) {
          // eslint-disable-next-line no-undef
          adobeAnalytics.createTarget({
            username: this.target.username,
            target: {
              name: this.target.display_name,
              type: this.target.target,
              id: createdTarget.id
            }
          })
          this.afterCreate(createdTarget)
        } else {
          this.afterSave(createdTarget)
        }
        this.saveCreateNotification()
        return createdTarget
      })
    },
    uploadTargetFile (createdTarget) {
      if (this.submittingFile && this.target.selectedFile) {  // only uploading if the user has not previously uploaded
        const file = this.target.selectedFile
        const splits = file.name.split('.')
        const extension = splits[splits.length - 1]
        const rt = new RecurringToast({
          type: 'info',
          title: 'Waiting for File To Upload',
          message: 'Please do not close browser, waiting for file to upload',
          interval: 5000
        })
        rt.start()
        return uploadTargetFile({target: createdTarget, file: {extension}})
          .then(resUploadTargetFile => {
            return uploadFile(resUploadTargetFile.data.signedURL, file).then(resp => {
              rt.stop()
              this.uploadNotification()
              this.afterUpload()
              return createdTarget
            })
          }).catch(ex => {
            rt.stop()
            Toast.error({
              title: 'Error Uploading the File',
              message: 'There was an error uploading the file',
              timeout: 15000
            })
            throw ex
          })
      } else {
        return new Promise(function (resolve, reject) {
          resolve(null)
        })
      }
    },
    submitTargetToATT (t) { // t = created or Updated Target
      if (this.submitted) {
        return AttAPI.updateATTCampaign({
          status: this.convertStatus(t.am_data.status),
          startDate: new Date(t.am_data.flightStartDate),
          endDate: new Date(t.am_data.AEFlightEndDate),
          cae_id: t.cae_id
        }).catch(err => {
          if (t.xandr_data.addressable) {
            Toast.error({
              title: 'Error Updating campaign to XD',
              message: 'There was an error submitting an update to Xandr Data',
              timeout: 15000
            })
          } else {
            Toast.error({
              title: 'Error Updating target to AE',
              message: 'There was an error submitting an update to Audience Engine',
              timeout: 15000
            })
          }
          throw err
        })
      } else {
        const selectedFile = this.target.am_data.selectedFileToUpload
        const isAB = selectedFile && selectedFile.type === ATT_SELECT_TYPE_OPTIONS.AUDIENCE_BUILDER
        // {display_name, advertiserName, startDate, endDate, cae_id, viewing_stream, fileName}
        return AttAPI.submitATTCampaign({
          display_name: t.display_name,
          target_id: t.id,
          advertiserName: t.advertiser.Name,
          startDate: new Date(t.am_data.flightStartDate),
          endDate: new Date(t.am_data.AEFlightEndDate),
          cae_id: t.cae_id,
          viewing_stream: this.convertViewingStream(t.viewing_stream),
          file: {
            // DEPRECATED, client is no longer submitting the file name on "UPLOAD"
            name: this.submittingFile ? '' : (isAB ? selectedFile.stringDataId : selectedFile.file),
            action: this.submittingFile ? 'UPLOAD' : (
              isAB ? 'AB' : 'SELECT'
            )
          }
        }).catch(err => {
          Toast.error({
            title: 'Error Submitting target to AE',
            message: 'There was an error submitting the target to Audience Engine',
            timeout: 15000
          })
          throw err
        })
      }
    },
    saveCreateNotification () {
      const submitTextFirstTime = `Successfully submitted the target: ${this.target.display_name}, you will be able to update it starting tomorrow.`
      const submittedText = `Successfully updated and submitted the target: ${this.target.display_name}.`
      const updatedText = `Successfully updated the target: ${this.target.display_name}.`
      const activateText = `Successfully created and activated the new target: ${this.target.display_name}`
      const isActivatedorWorkingXandrTarget = (this.target.activation_status === ACTIVATION_STATUS.ACTIVATED || this.target.activation_status === ACTIVATION_STATUS.WORKING) && this.target.target === TARGET_TYPE.XANDR_SEGMENT
      let text = this.submitted ? (this.isNew ? submitTextFirstTime : submittedText) : activateText
      if (isActivatedorWorkingXandrTarget) {
        text = updatedText
      }
      const title = this.isNew ? 'Target Created' : 'Target Updated'
      Toast.success({
        title,
        message: text,
        timeout: 15000
      })
    },
    uploadNotification () {
      Toast.success({
        title: 'Uploaded File',
        message: 'The target file has successfully been uploaded',
        timeout: 15000
      })
    },
    errorNotification () {
      Toast.error({
        title: 'Error processing target',
        message: 'There was an error while processing the target',
        timeout: 15000
      })
    },
    convertStatus (s) {
      if (s === ATT_STATUS.ORDERED) {
        return 'ORDERED'
      } else if (s === ATT_STATUS.CANCELLED) {
        return 'CANCELLED'
      } else {
        return 'PLANNING'
      }
    },
    convertViewingStream (vs) {
      if (vs === VIEWING_STREAMS.LIVE3) {
        return 'C3'
      } else if (vs === VIEWING_STREAMS.LSD) {
        return 'C0'
      }
    }
  }
  return ATTCreation
}



// WEBPACK FOOTER //
// ./src/app/appLogic/targets/createTarget/ATTCreation.js